// Pure helpers for the "Answers given" section of an application: grouping,
// labels, and deciding whether a value the user typed can be saved for reuse
// (as a keyed answer, a profile field or a custom answer). No React in here.

import {
  AnswerValue,
  ApplicationAnswer,
  CanonicalQuestion,
  FormTemplate,
  Profile,
  UserAnswer,
} from '../types';

// ---------------------------------------------------------------------------
// Never-store prompts
// ---------------------------------------------------------------------------

// Mirrors `never_store.label_patterns` in backend/apps/answers/taxonomy.json
// (applied server-side by apps/answers/secrets.py and by the extension's
// shared/secrets.js). Keep the three in sync: the API rejects these prompts
// with a 400, so the dashboard must not offer to save them.
const NEVER_STORE_PATTERNS: RegExp[] = [
  /\b(security|verification|verify|confirmation|authentication|auth|one[- ]?time|login|sign[- ]?in|2fa|mfa|two[- ]factor|email|sms)\s*(code|pin|token|passcode)\b/i,
  /\bcode\s+(we\s+|that\s+was\s+|was\s+)?(sent|emailed|texted)\b/i,
  /\benter\s+(the\s+|your\s+)?(\d+[- ]?(digit|character)\s+)?code\b/i,
  /\botp\b/i,
  /\bpass(word|code|phrase)\b/i,
  /\bcaptcha\b/i,
  /\b(cvv|cvc|csc)\b/i,
  /\bcard\s*(number|no)\b/i,
  /\bsocial\s+(security|insurance)\b/i,
  /\bssn\b/i,
  /\bnational\s+(id|identification|insurance)\b/i,
  /\bpassport\s*(number|no|#)/i,
  /\bdriver'?s?\s+licen[sc]e\s*(number|no|#)/i,
  /\b(bank\s+account|routing\s+number|iban|sort\s+code)\b/i,
  /\btax(payer)?\s*(id|identification)\b/i,
];

const collapseWhitespace = (text: string): string => text.replace(/\s+/g, ' ').trim();

/** True when a prompt asks for a one-time code or a secret that is never stored. */
export function isSecretPrompt(text: string | null | undefined): boolean {
  const cleaned = collapseWhitespace(String(text ?? ''));
  if (!cleaned) return false;
  return NEVER_STORE_PATTERNS.some((pattern) => pattern.test(cleaned));
}

// ---------------------------------------------------------------------------
// Prompt normalization
// ---------------------------------------------------------------------------

const NOISE_TOKENS = ['required', 'optional', 'please'];

/**
 * Comparable form of a prompt, following backend/apps/answers/normalize.py:
 * lowercase, "&" -> "and", apostrophes dropped, other punctuation to spaces,
 * noise tokens removed. Letters outside ASCII are kept, so two different
 * non-Latin prompts never collapse to the same string.
 */
export function normalizePrompt(text: string | null | undefined): string {
  return String(text ?? '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[’'`]/g, '')
    .replace(/[!-/:-@[-`{-~¡-¿‐-‧‰-⁞]/g, ' ')
    .split(/\s+/)
    .filter((token) => token && NOISE_TOKENS.indexOf(token) === -1)
    .join(' ');
}

// ---------------------------------------------------------------------------
// Grouping by how the field was filled
// ---------------------------------------------------------------------------

export interface FilledByMeta {
  value: string;
  label: string;
  description: string;
}

/** Display order: the actionable group (typed by the user) comes first. */
export const FILLED_BY_GROUPS: FilledByMeta[] = [
  {
    value: 'user',
    label: 'Typed by you',
    description: 'Values you entered yourself. Save the ones you want JobFlo to reuse.',
  },
  {
    value: 'auto',
    label: 'Filled by JobFlo',
    description: 'Filled from your profile and saved answers.',
  },
  {
    value: 'skipped',
    label: 'Skipped',
    description: 'JobFlo had no answer for these and no value was recorded.',
  },
];

export interface AnswerGroup extends FilledByMeta {
  answers: ApplicationAnswer[];
}

/**
 * One group per `filled_by` value, in display order. The three known groups
 * are always returned (possibly empty) so their counts can be shown; any
 * value the API adds later gets its own group after them.
 */
export function groupAnswersByFilledBy(answers: ApplicationAnswer[]): AnswerGroup[] {
  const groups: AnswerGroup[] = FILLED_BY_GROUPS.map((meta) => ({ ...meta, answers: [] }));
  answers.forEach((answer) => {
    const filledBy: string = answer.filled_by || 'auto';
    let group = groups.find((g) => g.value === filledBy);
    if (!group) {
      group = { value: filledBy, label: humanize(filledBy), description: '', answers: [] };
      groups.push(group);
    }
    group.answers.push(answer);
  });
  return groups;
}

function humanize(identifier: string): string {
  const words = identifier.replace(/[_.]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : identifier;
}

// ---------------------------------------------------------------------------
// Profile-backed keys
// ---------------------------------------------------------------------------

/** Profile fields a typed answer may be saved to. All are plain strings. */
export type SavableProfileField =
  | 'phone'
  | 'city'
  | 'country'
  | 'linkedin_url'
  | 'github_url'
  | 'portfolio_url'
  | 'website_url'
  | 'current_employer'
  | 'current_title';

export interface ProfileTarget {
  field: SavableProfileField;
  label: string;
}

/**
 * Canonical question key -> profile field. Name and email keys (`person.*`,
 * `contact.email`) and `address.location` are deliberately absent: identity
 * is edited on the profile page, and a combined location cannot be split
 * into city / region / country without guessing.
 */
export const PROFILE_FIELD_BY_KEY: Record<string, ProfileTarget> = {
  'contact.phone': { field: 'phone', label: 'Phone' },
  'address.city': { field: 'city', label: 'City' },
  'address.country': { field: 'country', label: 'Country' },
  'links.linkedin': { field: 'linkedin_url', label: 'LinkedIn' },
  'links.github': { field: 'github_url', label: 'GitHub' },
  'links.portfolio': { field: 'portfolio_url', label: 'Portfolio' },
  'links.website': { field: 'website_url', label: 'Website' },
  'employment.current_employer': { field: 'current_employer', label: 'Current employer' },
  'employment.current_title': { field: 'current_title', label: 'Current title' },
};

export function profileTargetForKey(key: string | null | undefined): ProfileTarget | null {
  if (!key) return null;
  return Object.prototype.hasOwnProperty.call(PROFILE_FIELD_BY_KEY, key)
    ? PROFILE_FIELD_BY_KEY[key]
    : null;
}

// Column limits on UserProfile (backend/apps/accounts/models.py).
const PROFILE_MAX_LENGTH: Partial<Record<SavableProfileField, number>> = {
  phone: 20,
  city: 120,
  country: 120,
  current_employer: 255,
  current_title: 255,
};

export type Conversion<T> = { ok: true; value: T } | { ok: false; reason: string };

/**
 * The string to store in a profile field for a recorded value, or the reason
 * it cannot be stored. Same rules the extension applies when it offers to
 * learn a typed value (extension/src/shared/learnables.js).
 */
export function toProfileValue(field: SavableProfileField, raw: string): Conversion<string> {
  const value = collapseWhitespace(String(raw ?? ''));
  if (!value) return { ok: false, reason: 'There is no value to save.' };

  if (/_url$/.test(field)) {
    if (/^https?:\/\//i.test(value)) return { ok: true, value };
    if (/^[\w-]+(\.[\w-]+)+(\/\S*)?$/.test(value)) return { ok: true, value: `https://${value}` };
    return {
      ok: false,
      reason: 'The recorded value does not look like a web address. Edit it on your profile instead.',
    };
  }

  const max = PROFILE_MAX_LENGTH[field];
  if (max && value.length > max) {
    return {
      ok: false,
      reason: `The recorded value is longer than this profile field allows (${max} characters).`,
    };
  }
  return { ok: true, value };
}

/** Whether the profile already holds this value (formatting differences ignored). */
export function profileValuesEqual(
  field: SavableProfileField,
  current: string | null | undefined,
  next: string
): boolean {
  if (current === null || current === undefined || current === '') return false;
  if (field === 'phone') {
    return String(current).replace(/\D/g, '') === String(next).replace(/\D/g, '');
  }
  const norm = (v: string) => collapseWhitespace(String(v)).toLowerCase().replace(/\/+$/, '');
  return norm(current) === norm(next);
}

// ---------------------------------------------------------------------------
// Keyed answers
// ---------------------------------------------------------------------------

const LIBRARY_HINT = 'Set it in the answer library instead.';

/**
 * Convert the string recorded on an application into the typed value
 * `PUT /answers/{key}/` expects for this question. The type mapping is the
 * one QuestionAnswerRow uses when it builds its payload (boolean -> boolean,
 * enum -> one of `options`, number -> number, everything else -> string) and
 * the one the API validates. Anything that would need a guess is refused.
 */
export function toAnswerValue(question: CanonicalQuestion, raw: string): Conversion<AnswerValue> {
  const text = String(raw ?? '').trim();
  if (!text) return { ok: false, reason: 'There is no value to save.' };

  switch (question.value_type) {
    case 'boolean':
      if (/^(yes|y|true)$/i.test(text)) return { ok: true, value: true };
      if (/^(no|n|false)$/i.test(text)) return { ok: true, value: false };
      return {
        ok: false,
        reason: `This is a yes/no question and the recorded value is not clearly yes or no. ${LIBRARY_HINT}`,
      };
    case 'enum': {
      const options = question.options ?? [];
      if (options.indexOf(text) !== -1) return { ok: true, value: text };
      const matches = options.filter((opt) => opt.toLowerCase() === text.toLowerCase());
      if (matches.length === 1) return { ok: true, value: matches[0] };
      return {
        ok: false,
        reason: `The recorded value is not one of the choices for this question (${options.join(', ')}). ${LIBRARY_HINT}`,
      };
    }
    case 'number':
      if (/^-?\d+(\.\d+)?$/.test(text)) return { ok: true, value: Number(text) };
      return {
        ok: false,
        reason: `This question expects a number and the recorded value is not a plain number. ${LIBRARY_HINT}`,
      };
    case 'date':
      if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return { ok: true, value: text };
      return {
        ok: false,
        reason: `This question expects a date and the recorded value is not in YYYY-MM-DD form. ${LIBRARY_HINT}`,
      };
    case 'file':
      return { ok: false, reason: 'Files are managed on the resumes page.' };
    default:
      return { ok: true, value: text };
  }
}

/** Whether the saved answer already equals the value about to be saved. */
export function answerValuesEqual(
  question: CanonicalQuestion,
  saved: AnswerValue,
  next: AnswerValue
): boolean {
  if (saved === null || saved === undefined) return false;
  let current: AnswerValue = saved;
  // Answers migrated from old templates can hold "Yes" for a boolean question.
  if (typeof saved === 'string') {
    const converted = toAnswerValue(question, saved);
    if (converted.ok) current = converted.value;
  }
  if (typeof current === 'string' && typeof next === 'string') {
    return collapseWhitespace(current) === collapseWhitespace(next);
  }
  return current === next;
}

/** A saved answer value as the user would read it. */
export function formatAnswerValue(value: AnswerValue): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (value === null || value === undefined) return '';
  return String(value);
}

// ---------------------------------------------------------------------------
// Custom answers
// ---------------------------------------------------------------------------

/** The custom answer saved for this prompt, if any (exact match wins). */
export function findCustomAnswer(templates: FormTemplate[], prompt: string): FormTemplate | null {
  const exact = templates.find((t) => t.question === prompt);
  if (exact) return exact;
  const normalized = normalizePrompt(prompt);
  if (!normalized) return null;
  return templates.find((t) => normalizePrompt(t.question) === normalized) ?? null;
}

// ---------------------------------------------------------------------------
// Row heading
// ---------------------------------------------------------------------------

export interface AnswerHeading {
  /** What the employer asked (falls back to the canonical label, then the key). */
  title: string;
  /** Canonical question label, when known and not just a repeat of the title. */
  matchedLabel: string | null;
}

export function describeAnswer(
  answer: ApplicationAnswer,
  questions: Record<string, CanonicalQuestion> | null
): AnswerHeading {
  const key = (answer.question_key || '').trim();
  const prompt = collapseWhitespace(answer.prompt_text || '');
  const question = key && questions ? questions[key] : undefined;
  const label = question?.label ?? profileTargetForKey(key)?.label ?? null;

  if (prompt) {
    const repeats = label !== null && normalizePrompt(label) === normalizePrompt(prompt);
    return { title: prompt, matchedLabel: label !== null && !repeats ? label : null };
  }
  if (label) return { title: label, matchedLabel: null };
  if (key) return { title: humanize(key.slice(key.lastIndexOf('.') + 1)), matchedLabel: null };
  return { title: 'Untitled question', matchedLabel: null };
}

// ---------------------------------------------------------------------------
// What can be done with a user-typed answer
// ---------------------------------------------------------------------------

/** Reference data the decision needs. `null` means not loaded (yet, or failed). */
export interface SaveLookups {
  questions: Record<string, CanonicalQuestion> | null;
  savedAnswers: Record<string, UserAnswer> | null;
  customAnswers: FormTemplate[] | null;
  profile: Profile | null;
}

export type SaveTarget = 'answer' | 'profile' | 'custom';

export type SaveAction =
  | { target: 'answer'; questionKey: string; label: string; value: AnswerValue }
  | { target: 'profile'; field: SavableProfileField; label: string; value: string }
  | { target: 'custom'; question: string; value: string; existingId: number | null };

export type SavePlan =
  /** Nothing to offer. `note` is a short user-facing reason, when one helps. */
  | { kind: 'none'; note?: string }
  /** The reference data needed to decide is not loaded. */
  | { kind: 'unavailable' }
  /** Already saved with an equivalent value. */
  | { kind: 'saved'; target: SaveTarget; verified: boolean }
  /** Cannot be saved from here without guessing. */
  | { kind: 'blocked'; target: SaveTarget; reason: string }
  /**
   * Can be saved. `replaces` is the value currently stored, present only
   * when saving would overwrite a different non-empty value.
   */
  | { kind: 'save'; action: SaveAction; replaces?: string };

const PROFILE_NOTE = 'Managed in your profile';

export function planSave(answer: ApplicationAnswer, lookups: SaveLookups): SavePlan {
  if (answer.filled_by !== 'user') return { kind: 'none' };
  const raw = String(answer.value ?? '');
  if (!raw.trim()) return { kind: 'none' };
  if (isSecretPrompt(answer.prompt_text)) return { kind: 'none' };

  const key = (answer.question_key || '').trim();
  if (!key) return planCustom(answer, raw, lookups);

  const question = lookups.questions ? lookups.questions[key] : undefined;
  if (question) return planAnswer(question, raw, lookups);

  const target = profileTargetForKey(key);
  if (target) return planProfile(target, raw, lookups);

  // Any other key might be an answerable question (person.pronouns is one);
  // that is only known once the question list has loaded.
  if (!lookups.questions) return { kind: 'unavailable' };
  // Loaded and not in it: the key is profile-backed without a writable
  // target here (name, email, address.location, ...) or belongs to an
  // education / experience entry. None of those are saved from this page.
  return { kind: 'none', note: PROFILE_NOTE };
}

function planAnswer(question: CanonicalQuestion, raw: string, lookups: SaveLookups): SavePlan {
  // File questions are not shown in the answer library either.
  if (question.value_type === 'file') return { kind: 'none' };
  if (!lookups.savedAnswers) return { kind: 'unavailable' };

  const converted = toAnswerValue(question, raw);
  if (!converted.ok) return { kind: 'blocked', target: 'answer', reason: converted.reason };

  const existing = lookups.savedAnswers[question.key];
  if (existing && answerValuesEqual(question, existing.value, converted.value)) {
    return { kind: 'saved', target: 'answer', verified: Boolean(existing.verified) };
  }
  const current = existing ? formatAnswerValue(existing.value) : '';
  return {
    kind: 'save',
    action: { target: 'answer', questionKey: question.key, label: question.label, value: converted.value },
    ...(current ? { replaces: current } : {}),
  };
}

function planProfile(target: ProfileTarget, raw: string, lookups: SaveLookups): SavePlan {
  if (!lookups.profile) return { kind: 'unavailable' };

  const converted = toProfileValue(target.field, raw);
  if (!converted.ok) return { kind: 'blocked', target: 'profile', reason: converted.reason };

  const current = lookups.profile[target.field];
  if (profileValuesEqual(target.field, current, converted.value)) {
    return { kind: 'saved', target: 'profile', verified: true };
  }
  return {
    kind: 'save',
    action: { target: 'profile', field: target.field, label: target.label, value: converted.value },
    ...(current ? { replaces: current } : {}),
  };
}

function planCustom(answer: ApplicationAnswer, raw: string, lookups: SaveLookups): SavePlan {
  const prompt = (answer.prompt_text || '').trim();
  // A custom answer is matched by its prompt; without one it cannot be reused.
  if (!prompt) return { kind: 'none' };
  if (!lookups.customAnswers) return { kind: 'unavailable' };

  const value = raw.trim();
  const existing = findCustomAnswer(lookups.customAnswers, prompt);
  if (existing && collapseWhitespace(existing.value) === collapseWhitespace(value)) {
    return { kind: 'saved', target: 'custom', verified: true };
  }
  return {
    kind: 'save',
    action: { target: 'custom', question: prompt, value, existingId: existing ? existing.id : null },
    ...(existing && existing.value ? { replaces: existing.value } : {}),
  };
}
